/* eslint-disable react/no-array-index-key */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ThreeDots } from 'react-loader-spinner';
import './css/AdminFlightChoice.css';
import {
  getTourDetails,
  getTourFlights,
  getOperatorInfo,
  applyCoupon,
} from '../../../Utils/RestUtils';
import { getOrderPayload } from '../../../Utils/OrderUtil';
import { isFlightInsuranceAvailable } from '../../../Utils/Constants/PriceUtil';
import {
  isCheapestFlight,
  isFlightValid,
  getCheapestFlightPrice,
  isOrderFlight,
} from '../../../Utils/FlightUtils';
import {
  FLIGHT_LOADING,
  RETRY,
  CANCEL,
  ORDER_FLIGHT_HINT,
  ORDER_FLIGHT_LOAD_ERROR,
  ORDER_FLIGHT_NO_OTHER,
} from '../../../Utils/Constants/AdminConstants';
import FlightOption from '../../FlightOption/FlightOption';

// the order tour actualized again: the flights and the data to count the price
const fetchFlightChoice = async (order) => {
  const [tourResponse, flightsResponse] = await Promise.all([
    getTourDetails(order.tourId),
    getTourFlights(order.tourId),
  ]);
  const tourData =
    tourResponse && tourResponse.data ? tourResponse.data.data : null;
  const flightsData = flightsResponse ? flightsResponse.data : null;

  if (
    !tourData ||
    tourData.success === 0 ||
    !tourData.tour ||
    !flightsData ||
    flightsData.iserror ||
    !flightsData.flights
  ) {
    return null;
  }

  const flights = flightsData.flights.filter(isFlightValid);

  if (flights.length === 0) {
    return null;
  }

  const acualizationData = tourData.tour;
  const [operatorResponse, promoResponse] = await Promise.all([
    getOperatorInfo(acualizationData.operatorcode).catch(() => null),
    order.promoCode ? applyCoupon(order.promoCode).catch(() => null) : null,
  ]);

  return {
    acualizationData,
    operatorInfo:
      operatorResponse && operatorResponse.data ? operatorResponse.data : {},
    tourInfo: flightsData.tourinfo || {},
    flights,
    promoCode:
      promoResponse && promoResponse.data && promoResponse.data.name
        ? promoResponse.data
        : null,
  };
};

// another flight of the order tour, the built order goes to handleSave,
// which resolves to true when the order is saved
function AdminFlightChoice(props) {
  const { order, isPending, handleSave, handleCancel } = props;
  const [choice, setChoice] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedFlight, setSelectedFlight] = useState(null);
  // a retry loads the flights again
  const [loadAttempt, setLoadAttempt] = useState(0);
  // drops the flights loaded after the form was closed
  const loadCounter = useRef(0);

  useEffect(() => {
    loadCounter.current += 1;
    const local = loadCounter.current;

    setChoice(null);
    setSelectedFlight(null);
    setIsLoading(true);
    setMessage('');

    fetchFlightChoice(order)
      .then((result) => {
        if (local !== loadCounter.current) {
          return;
        }

        if (!result) {
          setMessage(ORDER_FLIGHT_LOAD_ERROR);
        } else if (
          result.flights.every((flight) => isOrderFlight(order, flight))
        ) {
          setMessage(ORDER_FLIGHT_NO_OTHER);
        } else {
          setChoice(result);
        }
      })
      .catch(() => {
        if (local === loadCounter.current) {
          setMessage(ORDER_FLIGHT_LOAD_ERROR);
        }
      })
      .finally(() => {
        if (local === loadCounter.current) {
          setIsLoading(false);
        }
      });

    return () => {
      loadCounter.current += 1;
    };
  }, [order, loadAttempt]);

  // the order options stay: insurance, prepayment and promo code
  const buildOrder = (flight) => {
    const { acualizationData, operatorInfo, tourInfo, promoCode } = choice;

    return getOrderPayload({
      acualizationData,
      selectedFlight: flight,
      tourInfo,
      insuranceChecked:
        `${order.flightInsurance}` === 'true' &&
        isFlightInsuranceAvailable(acualizationData, operatorInfo),
      promoCode: promoCode || {},
      isPromoApplied: !!promoCode,
      splitPrice: parseInt(order.split, 10) || 100,
      priceCurrency: flight.price.currency,
      tourId: order.tourId,
      phoneNumber: order.phoneNumber || '',
      email: order.email || '',
      note: order.note || '',
    });
  };

  const isFlightSelected = (flight) =>
    (selectedFlight
      ? flight === selectedFlight
      : isOrderFlight(order, flight));

  // the chosen flight is saved at once, the parent closes the list after the save
  const selectFlight = (flight) => {
    if (isPending) {
      return;
    }

    setSelectedFlight(flight);

    handleSave(buildOrder(flight)).then((isSaved) => {
      if (!isSaved) {
        setSelectedFlight(null);
      }
    });
  };

  const renderSpinner = () => (
    <ThreeDots
      visible
      height="24"
      width="30"
      color="rgba(0, 0, 0, .5)"
      radius="5"
      ariaLabel="three-dots-loading"
    />
  );

  const renderFlightsList = () => {
    const { flights } = choice;
    const cheapestPrice = getCheapestFlightPrice(flights);

    return (
      <div className="afc_flights custom_scroll_container">
        {flights.map((flight, index) => (
          <FlightOption
            key={`afc-flight-${index}`}
            flight={flight}
            cheapestPrice={cheapestPrice}
            isSelected={isFlightSelected(flight)}
            cheapest={isCheapestFlight(flight, flights)}
            handleSelect={selectFlight}
          />
        ))}
      </div>
    );
  };

  const renderFooter = () => (
    <div className="afc_footer">
      {isPending && renderSpinner()}
      <button
        type="button"
        className="ap_small_btn light_gray_button bm_text_14"
        disabled={isPending}
        onClick={handleCancel}
      >
        {CANCEL}
      </button>
    </div>
  );

  return (
    <div className="afc_container">
      {isLoading && (
        <div className="afc_status gr_text_14">
          {renderSpinner()}
          {FLIGHT_LOADING}
        </div>
      )}
      {!isLoading && message && (
        <div className="afc_status">
          <span className="gr_text_14">{message}</span>
          {message === ORDER_FLIGHT_LOAD_ERROR && (
            <button
              type="button"
              className="ap_small_btn light_gray_button bm_text_14"
              onClick={() => setLoadAttempt(loadAttempt + 1)}
            >
              {RETRY}
            </button>
          )}
        </div>
      )}
      {choice && (
        <>
          <div className="gr_text_14">{ORDER_FLIGHT_HINT}</div>
          {renderFlightsList()}
        </>
      )}
      {renderFooter()}
    </div>
  );
}

AdminFlightChoice.propTypes = {
  order: PropTypes.instanceOf(Object).isRequired,
  isPending: PropTypes.bool.isRequired,
  handleSave: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
};

export default AdminFlightChoice;
